/**
 * =============================================================================
 * IDS CRM Web - Email Inbox (sales rework Phase 7, decision §0.8)
 * =============================================================================
 * Everything that lands at the CRM's shared mailbox. Users assign an email to
 * a customer (→ customer timeline) or customer + opportunity (→ the group's
 * history), or dismiss junk.
 * =============================================================================
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authedFileHref } from '../utils/files';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import './Inbox.css';

function AssignModal({ email, authFetch, onClose, onDone }) {
  const [customer, setCustomer] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setGroupId('');
    setGroups([]);
    if (!customer) return;
    (async () => {
      try {
        const response = await authFetch(`/api/v1/opportunity-groups?customer_id=${customer.customer_id}`);
        if (response.ok) setGroups((await response.json()).data || []);
      } catch (err) { /* groups optional */ }
    })();
  }, [customer, authFetch]);

  const assign = async () => {
    if (!customer) { setError('Pick a customer'); return; }
    setBusy(true);
    setError('');
    try {
      const response = await authFetch(`/api/v1/email-inbox/${email.email_id}/assign`, {
        method: 'PUT',
        body: JSON.stringify({ customer_id: customer.customer_id, opportunity_group_id: groupId || null })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to assign the email');
      }
      onDone();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Assign email</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          <p className="inbox-assign-summary"><strong>{email.subject || '(no subject)'}</strong> — from {email.from_name || email.from_address}</p>
          {customer ? (
            <div className="form-group">
              <label>Customer</label>
              <div className="entity-selected">
                <span>{customer.business_name}</span>
                <button type="button" className="btn-link" onClick={() => setCustomer(null)}>Change</button>
              </div>
            </div>
          ) : (
            <div className="form-group entity-picker">
              <label>Customer *</label>
              <CustomerSearchSelect
                id="inbox_customer_search"
                kind="any"
                minChars={1}
                value=""
                placeholder="Search customers…"
                onSelect={(row) => { if (row) setCustomer(row); }}
              />
            </div>
          )}
          {customer && (
            <div className="form-group">
              <label>Opportunity (optional — adds the email to its history)</label>
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                <option value="">Customer timeline only</option>
                {groups.map(group => (
                  <option key={group.group_id} value={group.group_id}>{group.group_ref} — {group.title}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={assign} disabled={busy || !customer}>
            {busy ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Inbox() {
  const { authFetch } = useAuth();
  const [emails, setEmails] = useState([]);
  const [status, setStatus] = useState('unassigned');
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [assignEmail, setAssignEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/api/v1/email-inbox?status=${status}`);
      if (!response.ok) throw new Error('Failed to load the inbox');
      const data = await response.json();
      setEmails(data.data || []);
      setUnassignedCount(data.unassigned_count || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, status]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (email) => {
    try {
      const response = await authFetch(`/api/v1/email-inbox/${email.email_id}/dismiss`, { method: 'PUT' });
      if (!response.ok) throw new Error('Failed to dismiss');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="inbox-page">
      <div className="page-header">
        <div>
          <h1>Inbox</h1>
          <p>Email captured at the CRM address — assign to a customer or opportunity. {unassignedCount} unassigned.</p>
        </div>
        <select className="pipeline-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="unassigned">Unassigned</option>
          <option value="assigned">Assigned</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>

      {error && <div className="form-error">{error}</div>}
      {loading ? (
        <div className="page-loading">Loading inbox…</div>
      ) : emails.length === 0 ? (
        <div className="empty-state">No {status} emails.</div>
      ) : (
        <div className="inbox-list">
          {emails.map(email => (
            <div key={email.email_id} className="inbox-item">
              <button
                type="button"
                className="inbox-item-head"
                onClick={() => setExpanded(expanded === email.email_id ? null : email.email_id)}
              >
                <span className="inbox-from">{email.from_name || email.from_address || 'Unknown sender'}</span>
                <span className="inbox-subject">{email.subject || '(no subject)'}</span>
                {(email.attachments || []).length > 0 && (
                  <span className="inbox-attachments">📎 {(email.attachments || []).length}</span>
                )}
                <span className="inbox-date">
                  {email.received_at ? new Date(email.received_at).toLocaleString('en-GB') : '—'}
                </span>
              </button>
              {expanded === email.email_id && (
                <div className="inbox-item-body">
                  <pre className="inbox-body-text">{email.body_text || '(no body captured)'}</pre>
                  {(email.attachments || []).filter(a => a?.url).map(attachment => (
                    <div key={attachment.attachment_id || attachment.url}>
                      <a href={authedFileHref(attachment.url)} target="_blank" rel="noreferrer">📎 {attachment.name}</a>
                    </div>
                  ))}
                  {email.assignment_status === 'assigned' && (
                    <p className="inbox-assigned-note">
                      Assigned to <Link to={`/customers/${email.customer_id}`}>{email.customer_name}</Link>
                      {email.group_ref && <> · <Link to={`/pipeline/groups/${email.opportunity_group_id}`}>{email.group_ref} — {email.group_title}</Link></>}
                      {email.assigned_by_name && <> by {email.assigned_by_name}</>}
                    </p>
                  )}
                  <div className="inbox-item-actions">
                    {email.assignment_status !== 'assigned' && (
                      <button className="btn-primary btn-small" onClick={() => setAssignEmail(email)}>Assign</button>
                    )}
                    {email.assignment_status === 'unassigned' && (
                      <button className="btn-secondary btn-small" onClick={() => dismiss(email)}>Dismiss</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {assignEmail && (
        <AssignModal
          email={assignEmail}
          authFetch={authFetch}
          onClose={() => setAssignEmail(null)}
          onDone={() => { setAssignEmail(null); load(); }}
        />
      )}
    </div>
  );
}

export default Inbox;
